'use client';

import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { observer, useMount, useObserve } from '@legendapp/state/react';
import type { ProductGiftProps } from './product-gift.type';
import { ProductGiftContents } from '../product-gift-contents';
// ProductGiftTitle is in the same directory
import { useProductGiftHook } from '../../hooks/product-gift.hook';
import { useGiftSelection } from '../../hooks/use-gift-selection.hook';
import { useDataTransformation } from '../../hooks/use-data-transformation.hook';
import { cartItemReferenceStoreAPI } from '@kidslabs/module-quote';
import { clearProductGiftsData } from '../../stores/product-gifts.store';
import type { SelectedGift } from '../../types/gift-selection.types';
import styles from './product-gift.module.css';
import { Text } from '../../../../utils';
import { Icon } from '@kidslabs/mario-nextjs-react-ui-core';
import { useTranslation } from 'next-i18next';
// Import ProductGiftTitle component inline
const ProductGiftTitle: FC<{ title: string }> = ({ title }) => {
    return (
        <div className={styles.titleContainer}>
            <Icon
                type="data-uri"
                dataUriProps={{
                    src: 'https://static.kidsplaza.vn/assets/icons/active-gift.png',
                    alt: title,
                }}
                size="xlarge"
            />
            <Text variant="h2" size="small" weight="bold" className={styles.titleText}>
                {title}
            </Text>
        </div>
    );
};

/**
 * ProductGift - Main component for displaying gifts on Product Detail Page
 * Converted from React Native to Next.js using UI Core components
 *
 * Key behaviors (SAME AS MOBILE):
 * - Listens to quantityStepper from cartItemReference store
 * - Updates counterValue when quantityStepper changes (from CartCounterButton)
 * - Updates gift options based on product quantity
 * - Resets quantityStepper to 1 on component unmount
 */
export const ProductGift: FC<ProductGiftProps> = observer(({ layout, product }) => {
    const { t } = useTranslation('flash-sale');

    const quantityStepper$ = cartItemReferenceStoreAPI.state.getQuantityStepperObservable();
    const setQuantityStepper = cartItemReferenceStoreAPI.actions.setQuantityStepper;
    // Counter value for product quantity - synced with quantityStepper
    const [counterValue, setCounterValue] = useState(1);

    /**
     * Session ID State:
     * - Initialize as empty string, will be set on mount
     * - Stable across re-renders once initialized
     * - Uses product ID and 'product' context for PDP page
     */
    const [sessionId, setSessionId] = useState<string>('');

    // Track previous product ID to detect configurable variant changes
    const previousProductIdRef = useRef<string | number | undefined>(product?.id);

    // Use the custom hook to sync selected gifts to cart
    const { sortedGiftsDataList, error } = useProductGiftHook({
        product,
        fetchGiftsFromCache: false,
    });

    const { availableGifts } = useDataTransformation({
        sortedGiftsDataList,
    });

    /**
     * Gift Selection with Intelligent Hook:
     * - Hook handles ALL gift logic
     * - Session ID is created via giftSelectionUtils.generateSessionId()
     * - Hook automatically saves sessionId to store when initializeSession
     */
    const giftHook = useGiftSelection();

    const activeSession = useMemo(() => {
        return giftHook.getActiveSessionId(`${product?.id}`, 'product');
    }, [giftHook, product?.id]);

    // Initialize session when component mounts (SAME AS MOBILE - useMount)
    useMount(() => {
        // Initialize session with the new sessionId
        const newSessionId = giftHook.initializeSession({
            sessionId: undefined,
            entityId: `${product?.id}`,
            entityType: 'product',
            availableGifts,
            productQuantity: counterValue,
        });

        // Set sessionId state
        setSessionId(newSessionId);

        // Auto-select best gift for first round
        giftHook.autoSelectBestGift(newSessionId, 1);
    });

    /**
     * Handle product change when user selects a different configurable variant.
     * When selectedVariant changes in parent (details.tsx), a new product (with different ID/SKU)
     * is passed here. We must destroy the old gift session and re-initialize for the new product.
     */
    useEffect(() => {
        const currentProductId = product?.id;
        const previousProductId = previousProductIdRef.current;

        // Skip initial mount (handled by useMount above)
        if (previousProductId === undefined || currentProductId === previousProductId) {
            previousProductIdRef.current = currentProductId;
            return;
        }

        console.log('[ProductGift] Product changed (attribute change):', {
            previousProductId,
            currentProductId,
            newSku: product?.sku,
        });

        // Destroy old session for previous product
        if (sessionId) {
            giftHook.destroySession(sessionId);
        }

        // Clear old product gift cache
        if (previousProductId) {
            clearProductGiftsData(`${previousProductId}`);
        }

        // Reset counter value to 1 for new variant
        setCounterValue(1);
        setQuantityStepper(1);

        // Initialize new session for the new product variant
        const newSessionId = giftHook.initializeSession({
            sessionId: undefined,
            entityId: `${currentProductId}`,
            entityType: 'product',
            availableGifts,
            productQuantity: 1,
        });

        setSessionId(newSessionId);
        giftHook.autoSelectBestGift(newSessionId, 1);

        // Update ref to track current product
        previousProductIdRef.current = currentProductId;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product?.id]);

    // Sync quantityStepper with counterValue (SAME AS MOBILE)
    useEffect(() => {
        setQuantityStepper(counterValue);
    }, [counterValue, setQuantityStepper]);

    // Update session when availableGifts changes (from API)
    // Note: counterValue changes are handled by useObserve above
    useEffect(() => {
        if (availableGifts.length > 0 && sessionId) {
            giftHook.updateSession(sessionId, availableGifts, counterValue);
            // Auto-select best gift when gifts data loaded/changed
            giftHook.autoSelectBestGift(sessionId, 1);
        }
        // Only re-run when availableGifts changes, not counterValue (handled by useObserve)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableGifts, sessionId]);

    // Debug log on mount (SAME AS MOBILE)
    useMount(() => {
        console.log('quantityStepper$.get() on mount', quantityStepper$.get());
    });

    // Listen to quantityStepper changes and handle gift selection reactively
    useObserve(quantityStepper$, ({ value }) => {
        const newQuantity = value ?? 1;

        // Update local counter value
        setCounterValue(newQuantity);

        // If session exists and availableGifts loaded, update session and auto-select for round 1
        if (sessionId && availableGifts.length > 0) {
            // Update session with new quantity first
            giftHook.updateSession(sessionId, availableGifts, newQuantity);

            // Clear all selected gifts when quantity changes
            giftHook.clearSession(sessionId);

            // Auto select best gift
            giftHook.autoSelectBestGift(sessionId, 1);

            console.log('[ProductGift] Stepper changed, clear all selected gifts:', {
                newQuantity: newQuantity,
                sessionId: sessionId,
            });
        }
    });

    useEffect(() => {
        if (sessionId !== activeSession) {
            giftHook.setActiveSession(sessionId, `${product.id}`, 'product');
        }
    }, [activeSession, sessionId, giftHook, product.id]);

    // Cleanup on unmount and reload - reset quantityStepper to 1
    useEffect(() => {
        const handleBeforeUnload = () => {
            clearProductGiftsData(product?.sku ?? '');
            setQuantityStepper(1);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            clearProductGiftsData(product?.sku ?? '');
            setQuantityStepper(1);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product?.sku, setQuantityStepper]);

    if (error) {
        return (
            <div className={styles.errorContainer}>
                <Text size="small" className={styles.errorText}>
                    {error.message}
                </Text>
            </div>
        );
    }

    if (sortedGiftsDataList.length === 0) {
        return null;
    }

    // Get observable and create selectGift handler
    const selectedGifts$ = giftHook.getStoreObservable(sessionId)?.selectedGifts;
    const handleSelectGift = (gift: SelectedGift) => {
        giftHook.selectGift(sessionId, gift, 1); // Round 1
    };

    // Auto-fill handler: when user selects an ineligible gift, bump quantity to meet threshold
    const handleAutoFillRequired = (requiredQuantity: number, gift: SelectedGift) => {
        // 1. Update the quantity stepper observable (UI will react)
        setQuantityStepper(requiredQuantity);
        setCounterValue(requiredQuantity);

        // 2. Update session with new quantity
        if (sessionId) {
            giftHook.updateSession(sessionId, availableGifts, requiredQuantity);
        }

        // 3. Recalculate and select the gift with updated quantity
        const rule = gift.giftData?.rule;
        if (rule) {
            const buyX = rule.discount_step || 1;
            const getY = rule.discount_amount || 1;
            const eligibleSets = Math.floor(requiredQuantity / buyX);
            const maxFreeGifts = eligibleSets * getY;

            const updatedGift: SelectedGift = {
                ...gift,
                quantity: maxFreeGifts,
                giftSlots: maxFreeGifts,
                quantityUsedByRule: eligibleSets * buyX,
            };

            giftHook.selectGift(sessionId, updatedGift, 1);
        }
    };

    return (
        <div className={styles.giftCard} data-testid="block-gift">
            <div className={styles.cardTitle}>
                <ProductGiftTitle title={t('text_gift_for_you')} />
            </div>
            <div className={styles.cardContent}>
                <ProductGiftContents
                    layout={layout}
                    counterValue={counterValue}
                    selectedGifts$={selectedGifts$}
                    sortedGiftsDataList={sortedGiftsDataList}
                    onSelectGift={handleSelectGift}
                    sessionId={sessionId}
                    enableAutoFill={true}
                    onAutoFillRequired={handleAutoFillRequired}
                />
            </div>
        </div>
    );
});

export default ProductGift;
